import React, { createContext, useCallback, useContext, useMemo } from "react";
import type { NotificationGroup, NotificationItemModel } from "./types";
import { useGetNotificationsQuery, useMarkAllNotificationsAsReadMutation } from "../../app/appApiSlice";
import { useAppSelector } from "../../app/hooks";
import { selectResourceId } from "../../app/auth/createAuthSlice";

interface NotificationContextValue {
  notifications: NotificationItemModel[];
  unreadCount: number;
  previewNotifications: NotificationItemModel[];
  markAllAsRead: () => void;
  groupedNotifications: Record<NotificationGroup, NotificationItemModel[]>;
  isLoading: boolean;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined
);

const groupOrder: NotificationGroup[] = ["Today", "Yesterday", "Earlier this week"];

export const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const userId = useAppSelector(selectResourceId);

  const { data: notifications = [], isLoading } = useGetNotificationsQuery(userId!, {
    skip: !userId,
    pollingInterval: 4000,
    skipPollingOnFocusLoss: true,
  });
  const [triggerMarkAllRead] = useMarkAllNotificationsAsReadMutation();

  const markAllAsRead = useCallback(async () => {
    if (!userId) return;
    await triggerMarkAllRead(userId);
  }, [userId, triggerMarkAllRead]);

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.isRead).length,
    [notifications]
  );

  const previewNotifications = useMemo(() => notifications.slice(0, 4), [notifications]);

  const groupedNotifications = useMemo(() => {
    return groupOrder.reduce(
      (acc, group) => {
        acc[group] = notifications.filter((item) => item.group === group);
        return acc;
      },
      {
        Today: [],
        Yesterday: [],
        "Earlier this week": [],
      } as Record<NotificationGroup, NotificationItemModel[]>
    );
  }, [notifications]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      previewNotifications,
      markAllAsRead,
      groupedNotifications,
      isLoading,
    }),
    [notifications, unreadCount, previewNotifications, markAllAsRead, groupedNotifications, isLoading]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }

  return context;
};
